/**
 * Cloudinary Composite URL Generation Utilities
 *
 * This module provides helpers for generating Cloudinary URLs with:
 * - Crop transformations based on viewport state (zoom/pan)
 * - Marker overlay positioning
 * - Combined crop + annotation URLs
 *
 * ## Usage Guide
 *
 * ### For Simple Position-Based Viewers
 * If your viewer uses direct CSS positioning (e.g., transform: translate(-300px, -200px)):
 * ```typescript
 * import { calculateCropFromPosition, generateCroppedAndAnnotatedUrl } from '@/utils/cloudinaryComposite';
 *
 * // 1. Get viewer state
 * const position = { x: imageElement.x, y: imageElement.y };  // Typically negative when panned
 * const marker = { x: 800, y: 600 };  // Marker in original image pixels
 *
 * // 2. Calculate crop
 * const crop = calculateCropFromPosition(
 *   { width: 2000, height: 1500 },  // Original image size
 *   { width: 500, height: 400 },    // Container size
 *   position,
 *   1.5  // Zoom level
 * );
 *
 * // 3. Generate Cloudinary URL
 * const url = generateCroppedAndAnnotatedUrl(originalUrl, viewerState, marker);
 * ```
 *
 * ### For Center-Based Viewers
 * If your viewer tracks a crop center point and pan offsets:
 * ```typescript
 * import { calculateCropFromViewerState, generateCroppedAndAnnotatedUrl } from '@/utils/cloudinaryComposite';
 *
 * const viewerState = {
 *   imageWidth: 2000,
 *   imageHeight: 1500,
 *   containerWidth: 500,
 *   containerHeight: 400,
 *   zoom: 1.5,
 *   panX: -100,
 *   panY: -50,
 *   cropCenterX: 1000,
 *   cropCenterY: 750,
 * };
 * const crop = calculateCropFromViewerState(viewerState);
 * const url = generateCroppedAndAnnotatedUrl(originalUrl, viewerState, marker);
 * ```
 *
 * ## BASE_SCALE Constant
 * The value 0.75 represents the base CSS scale applied to images in the viewer.
 * This ensures the image fits within the container while maintaining a margin.
 * If your viewer uses a different base scale, adjust this constant accordingly.
 */

const BASE_SCALE = 0.75;

/**
 * Crop coordinates calculated from viewer state
 */
export interface CropCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Viewer state for calculating crop
 */
export interface ViewerCropState {
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  containerHeight: number;
  zoom: number;
  panX: number;
  panY: number;
  cropCenterX: number;
  cropCenterY: number;
}

/**
 * Marker position
 */
export interface MarkerPosition {
  x: number;
  y: number;
}

/**
 * Calculate crop coordinates from viewer position (simpler variant for direct position-based viewers)
 *
 * This function converts CSS pixel positions from a zoomed/panned viewer back to original image pixels.
 * Position values are typically negative when the image is shifted (e.g., position.x = -300 means
 * the image is shifted 300px to the left, revealing the right side).
 *
 * @param imageSize - Original image dimensions { width, height }
 * @param containerSize - Visible container dimensions { width, height }
 * @param position - Image position in CSS pixels { x, y } (negative = shifted left/up)
 * @param zoomLevel - Zoom multiplier (1.0 = 100%, 2.0 = 200%)
 * @returns Crop coordinates in original image pixel space
 *
 * @example
 * // Original image: 2000x1500px, Container: 500x400px, Zoom: 1.5x, Position: { x: -300, y: -200 }
 * calculateCropFromPosition(
 *   { width: 2000, height: 1500 },
 *   { width: 500, height: 400 },
 *   { x: -300, y: -200 },
 *   1.5
 * )
 * // scale = 0.75 * 1.5 = 1.125
 * // cropX = -(-300) / 1.125 = 267
 * // cropY = -(-200) / 1.125 = 178
 * // cropWidth = 500 / 1.125 = 444
 * // cropHeight = 400 / 1.125 = 356
 * // Returns: { x: 267, y: 178, width: 444, height: 356 }
 */
export function calculateCropFromPosition(
  imageSize: { width: number; height: number },
  containerSize: { width: number; height: number },
  position: { x: number; y: number },
  zoomLevel: number
): CropCoordinates {
  // IMPORTANT: 0.75 is a base scale factor used in the viewer CSS
  const scale = BASE_SCALE * zoomLevel;

  // Convert from CSS pixels back to original image pixels
  // position.x is negative when image is shifted left (cropping from left)
  const cropX = Math.max(0, Math.round(-position.x / scale));
  const cropY = Math.max(0, Math.round(-position.y / scale));

  // Calculate visible area in original image pixels
  const visibleWidth = Math.round(Math.min(containerSize.width / scale, imageSize.width - cropX));
  const visibleHeight = Math.round(Math.min(containerSize.height / scale, imageSize.height - cropY));

  return {
    x: cropX,
    y: cropY,
    width: visibleWidth,
    height: visibleHeight,
  };
}

/**
 * Calculate crop coordinates from viewer state (legacy variant using cropCenter)
 *
 * This determines what portion of the original image is visible in the viewport
 * based on the current zoom level and pan offset.
 *
 * @example
 * // If zoom = 2.0, the visible area is containerWidth / (BASE_SCALE * zoom) in original pixels
 * // The panX and panY determine where in the original image this crop starts
 * calculateCropFromViewerState({
 *   imageWidth: 1920,
 *   imageHeight: 1080,
 *   containerWidth: 800,
 *   containerHeight: 600,
 *   zoom: 2.0,
 *   panX: -200,
 *   panY: -100,
 *   cropCenterX: 960,
 *   cropCenterY: 540
 * })
 * // Returns: { x: 200, y: 100, width: 533, height: 400 }
 */
export function calculateCropFromViewerState(state: ViewerCropState): CropCoordinates {
  const { imageWidth, imageHeight, containerWidth, containerHeight, zoom, panX, panY, cropCenterX, cropCenterY } = state;

  const scale = BASE_SCALE * zoom;
  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;

  // Calculate crop position centered on cropCenter
  const scaledCropCenterX = cropCenterX * scale;
  const scaledCropCenterY = cropCenterY * scale;
  const baseCropX = Math.max(0, scaledCropCenterX - containerWidth / 2);
  const baseCropY = Math.max(0, scaledCropCenterY - containerHeight / 2);

  // Apply pan offset
  let cropX = baseCropX - panX;
  let cropY = baseCropY - panY;

  // Clamp to image bounds
  const maxScrollX = Math.max(0, scaledWidth - containerWidth);
  const maxScrollY = Math.max(0, scaledHeight - containerHeight);
  cropX = Math.max(0, Math.min(cropX, maxScrollX));
  cropY = Math.max(0, Math.min(cropY, maxScrollY));

  // Convert from scaled coordinates back to original image coordinates
  const origCropX = Math.round(cropX / scale);
  const origCropY = Math.round(cropY / scale);
  const origCropWidth = Math.round(Math.min(containerWidth / scale, imageWidth - origCropX));
  const origCropHeight = Math.round(Math.min(containerHeight / scale, imageHeight - origCropY));

  return {
    x: origCropX,
    y: origCropY,
    width: origCropWidth,
    height: origCropHeight,
  };
}

/**
 * Calculate marker position relative to a crop area
 *
 * Converts absolute marker coordinates (in original image pixels) to
 * positions relative to the cropped area, optionally scaled to an output size.
 *
 * @param marker - Marker position in original image coordinates
 * @param crop - Crop area in original image coordinates
 * @param maxOutputWidth - Maximum output width (for c_fit sizing)
 * @param maxOutputHeight - Maximum output height (for c_fit sizing)
 *
 * @example
 * calculateMarkerRelativeToCrop(
 *   { x: 350, y: 180 },           // Marker at (350, 180) in original image
 *   { x: 200, y: 100, width: 400, height: 300 },  // Crop area (4:3 ratio)
 *   1200,  // Max output width
 *   800    // Max output height
 * )
 * // Crop ratio: 400/300 = 1.33
 * // Max ratio: 1200/800 = 1.5
 * // Since crop is taller than max, height is limiting: scale = 800/300 = 2.67
 * // Actual output: 1067x800
 * // Relative position: x = 350 - 200 = 150, y = 180 - 100 = 80
 * // Scaled: x = 150 * 2.67 = 400, y = 80 * 2.67 = 213
 * // Returns: { x: 400, y: 213, isVisible: true }
 */
export function calculateMarkerRelativeToCrop(
  marker: MarkerPosition,
  crop: CropCoordinates,
  maxOutputWidth?: number,
  maxOutputHeight?: number
): { x: number; y: number; isVisible: boolean } {
  // Check if marker is within the crop area
  const isVisible =
    marker.x >= crop.x &&
    marker.x <= crop.x + crop.width &&
    marker.y >= crop.y &&
    marker.y <= crop.y + crop.height;

  // Calculate relative position within crop
  let relativeX = marker.x - crop.x;
  let relativeY = marker.y - crop.y;

  // Scale to output size if provided (accounting for c_fit aspect ratio preservation)
  if (maxOutputWidth && maxOutputHeight && crop.width > 0 && crop.height > 0) {
    const cropRatio = crop.width / crop.height;
    const maxRatio = maxOutputWidth / maxOutputHeight;

    // Determine which dimension is limiting (c_fit behavior)
    const scaleFactor = cropRatio > maxRatio
      ? maxOutputWidth / crop.width   // Width is limiting
      : maxOutputHeight / crop.height; // Height is limiting

    relativeX = Math.round(relativeX * scaleFactor);
    relativeY = Math.round(relativeY * scaleFactor);
  } else if (maxOutputWidth && crop.width > 0) {
    // Legacy: scale by width only
    const scaleFactor = maxOutputWidth / crop.width;
    relativeX = Math.round(relativeX * scaleFactor);
    relativeY = Math.round(relativeY * scaleFactor);
  } else {
    relativeX = Math.round(relativeX);
    relativeY = Math.round(relativeY);
  }

  return { x: relativeX, y: relativeY, isVisible };
}

/**
 * Extract public ID from a Cloudinary URL
 *
 * Handles URLs in the format:
 * https://res.cloudinary.com/{cloud_name}/image/upload/{transformations}/{public_id}
 */
export function extractCloudinaryPublicId(url: string): { baseUrl: string; publicId: string } | null {
  if (!url || !url.includes('cloudinary')) {
    return null;
  }

  try {
    const urlParts = url.split('/upload/');
    if (urlParts.length !== 2) return null;

    const baseUrl = urlParts[0] + '/upload';
    const publicIdPart = urlParts[1];

    // Extract public ID (skip existing transformations)
    const segments = publicIdPart.split('/');
    const transformPatterns = /^[a-z]_|^f_|^q_|^dpr_|^fl_|^bo_|^co_|^b_|^o_|^l_|^g_|^x_|^y_/;
    let publicId = '';

    for (const segment of segments) {
      if (!transformPatterns.test(segment)) {
        publicId += (publicId ? '/' : '') + segment;
      }
    }

    if (!publicId) {
      publicId = segments[segments.length - 1];
    }

    return { baseUrl, publicId };
  } catch {
    return null;
  }
}

/**
 * Generate a Cloudinary URL with crop transformation
 *
 * Creates a URL that crops the image to show only the visible portion
 * based on the viewer state.
 */
export function generateCroppedUrl(
  originalUrl: string,
  viewerState: ViewerCropState
): string {
  const parsed = extractCloudinaryPublicId(originalUrl);
  if (!parsed) return originalUrl;

  const crop = calculateCropFromViewerState(viewerState);
  const cropTransform = `c_crop,x_${crop.x},y_${crop.y},w_${crop.width},h_${crop.height}`;

  // Standard output dimensions - use c_fit to maintain aspect ratio
  const OUTPUT_WIDTH = 1200;
  const OUTPUT_HEIGHT = 800;
  const scaleTransform = `c_fit,w_${OUTPUT_WIDTH},h_${OUTPUT_HEIGHT}`;

  return `${parsed.baseUrl}/${cropTransform}/${scaleTransform}/${parsed.publicId}`;
}

/**
 * Generate a Cloudinary URL with marker overlay
 *
 * Creates a URL with an arrow marker (➜) overlay at the specified position.
 * The marker has an olive/yellow-green background matching the UI style.
 */
export function generateAnnotatedUrl(
  originalUrl: string,
  marker: MarkerPosition
): string {
  const parsed = extractCloudinaryPublicId(originalUrl);
  if (!parsed) return originalUrl;

  // Arrow marker: ➜ encoded
  const markerText = encodeURIComponent('➜');
  const markerColor = 'd8e364'; // Olive/yellow-green
  const textColor = 'FFFFFF'; // White text
  const fontSize = 24; // Standard font size

  // Estimate marker size (font size + background padding)
  // The background padding in Cloudinary is approximately 8-10px on each side
  const markerWidth = fontSize + 16;  // ~40px total
  const markerHeight = fontSize + 16; // ~40px total

  // Offset to center the marker on the coordinate (g_north_west positions top-left corner)
  const offsetX = Math.round(marker.x - markerWidth / 2);
  const offsetY = Math.round(marker.y - markerHeight / 2);

  // Add letter spacing to make arrow wider without making it taller
  const markerOverlay = `l_text:Roboto_${fontSize}_bold_letter_spacing_8:${markerText},co_rgb:${textColor},b_rgb:${markerColor}/fl_layer_apply,g_north_west,x_${offsetX},y_${offsetY}`;

  return `${parsed.baseUrl}/${markerOverlay}/${parsed.publicId}`;
}

/**
 * Generate a Cloudinary URL with both crop and marker overlay
 *
 * This is the most common use case - showing a cropped viewport with a marker.
 * The marker position is adjusted relative to the crop area.
 */
export function generateCroppedAndAnnotatedUrl(
  originalUrl: string,
  viewerState: ViewerCropState,
  marker: MarkerPosition
): string {
  const parsed = extractCloudinaryPublicId(originalUrl);
  if (!parsed) return originalUrl;

  // Calculate crop
  const crop = calculateCropFromViewerState(viewerState);

  // Standard output dimensions - use c_fit to maintain aspect ratio
  const OUTPUT_WIDTH = 1200;
  const OUTPUT_HEIGHT = 800;

  // Calculate marker position relative to the crop and scaled to output dimensions
  const relativeMarker = calculateMarkerRelativeToCrop(marker, crop, OUTPUT_WIDTH, OUTPUT_HEIGHT);

  // Build transformations
  const cropTransform = `c_crop,x_${crop.x},y_${crop.y},w_${crop.width},h_${crop.height}`;
  // Use c_fit instead of c_scale to maintain aspect ratio (won't squish)
  const scaleTransform = `c_fit,w_${OUTPUT_WIDTH},h_${OUTPUT_HEIGHT}`;

  // Only add marker if it's visible in the crop
  if (relativeMarker.isVisible) {
    const markerText = encodeURIComponent('➜');
    const markerColor = 'd8e364';
    const textColor = 'FFFFFF'; // White text
    const fontSize = 24; // Standard font size

    // Estimate marker size (font size + background padding)
    // The background padding in Cloudinary is approximately 8-10px on each side
    const markerWidth = fontSize + 16;  // ~40px total
    const markerHeight = fontSize + 16; // ~40px total

    // Offset to center the marker on the coordinate (g_north_west positions top-left corner)
    const offsetX = Math.round(relativeMarker.x - markerWidth / 2);
    const offsetY = Math.round(relativeMarker.y - markerHeight / 2);

    // Add letter spacing to make arrow wider without making it taller
    const markerOverlay = `l_text:Roboto_${fontSize}_bold_letter_spacing_8:${markerText},co_rgb:${textColor},b_rgb:${markerColor}/fl_layer_apply,g_north_west,x_${offsetX},y_${offsetY}`;

    return `${parsed.baseUrl}/${cropTransform}/${scaleTransform}/${markerOverlay}/${parsed.publicId}`;
  }

  // No marker visible, just crop and scale
  return `${parsed.baseUrl}/${cropTransform}/${scaleTransform}/${parsed.publicId}`;
}

/**
 * Encode text for safe use in Cloudinary URL transformations
 *
 * Handles special characters that could break the transformation syntax.
 */
export function encodeTextForCloudinary(text: string): string {
  return text
    .replace(/%/g, '%25')
    .replace(/,/g, '%2C')
    .replace(/\//g, '%2F')
    .replace(/:/g, '%3A')
    .replace(/\s/g, '%20')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29')
    .replace(/'/g, '%27')
    .replace(/"/g, '%22')
    .replace(/&/g, '%26')
    .replace(/\+/g, '%2B');
}
